import { gql } from "@apollo/client";
import {
    faBasketShopping,
    faCalculator,
    faCalendar,
    faCheckToSlot,
    faEye,
    faLayerGroup,
    faList,
    faObjectGroup,
    faPeopleGroup,
    faSave,
    faSeedling,
    faTimeline,
    faWeightHanging
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import AppLink from "components/common/AppLink";
import Button from "components/common/button/Button";
import { ComponentStateTypes } from "components/common/ComponentState";
import Datalist, { DatalistPropsType } from "components/common/datalist/Datalist";
import Input from "components/common/form/inputs/Input";
import Page from "components/common/page/Page";
import { uuid } from "config/app-constants";
import { api, graph } from "config/axios-context";
import { AppRoutes } from "config/router";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataSourceRequestType } from "types/datalist-types";
import { HarvestFilterInput, HarvestMultiUpdateInput, HarvestMultiUpdateMutation, HarvestMultiUpdateMutationVariables, HarvestMultiUploadDataQuery, HarvestMultiUploadDataQueryVariables, HarvestSortInput, SortEnumType } from "types/graphql";
import { formatMoney, resolveItemViewValue } from "utilities/datalist/datalist-sort-utilities";
import useAppContext from "utilities/hooks/useAppContext";


const TOTAL_ROW_INDEX = -1;

const query = gql`
query harvestMultiUploadData(
    $take: Int
    $skip: Int
    $where: HarvestFilterInput
    $order: [HarvestSortInput!]
  ) {
    harvests {
      count(where: $where)
      items(where: $where, order: $order, skip: $skip, take: $take) {
        fruit {
          name
          id
        }
        harvestFruitCategories {
          items {
            fruitCategory {
              name
            }
          }
        }
        harvestFruitGroups {
          items {
            fruitGroup {
              name
            }
          }
        }
        kg
        cartQuantity,
        hideFromMultiUpdate,
        id
        work {
          id
          startDate
          season {
            name
            id
          }
          workers {
            count
            priceSum
          }
        }
      }
    }
  }  
`;

const harvestMultiUpload = gql`
mutation harvestMultiUpdate($data: [HarvestMultiUpdateInput!]!) {
    harvestMultiUpdate(harvests: $data)
  }
`;

type HarvestType = Omit<Exclude<Exclude<Exclude<HarvestMultiUploadDataQuery['harvests'], null | undefined>['items'], null | undefined>[0], null | undefined>, 'kg' | 'cartQuantity'> & {
    realKg: number;
    kg: number | string;
    cartQuantity: number | string;
}

const HarvestMultiUploadPage = () => {
    const [t] = useTranslation();
    const context = useAppContext();
	const [title, setTitle] = useState<string>(t('pages.agriculture.work.harvest-multi-update'));
    const [kgPerCart, setKgPerCart] = useState<string>('');
    const [componentState, setComponentState] = useState<ComponentStateTypes>('loading');
    const [data, setData] = useState<HarvestType[]>([]);
	const datalistRef = useRef<Datalist<HarvestType, HarvestFilterInput, HarvestSortInput>>(null);

	const pageActions = [
		{
			icon: faList,
			link: AppRoutes.AGRICULTURE_OVERVIEW_WORK,
            name: 'list',
            title: t('pages.agriculture.work.harvest.list')
		}
	]

    const datalistConfig: DatalistPropsType<HarvestType, HarvestFilterInput, HarvestSortInput> = {
        dataSource: {
            data: options => {
                let dataList = [...data];
                if (!options) return dataList;

                if (options.sort?.length) {
                    const sort = options.sort[0];
                    const dir: 'asc' | 'desc' = sort.work!.startDate === SortEnumType.Asc ? 'asc' : 'desc';
                    dataList = dataList.sort((a, b) => {
                        if (a.id === TOTAL_ROW_INDEX) return 1;
                        if (b.id === TOTAL_ROW_INDEX) return -1;
                        return dir === 'asc' ? a.work.startDate.localeCompare(b.work.startDate) : b.work.startDate.localeCompare(a.work.startDate);
                    });
                }

                return dataList;
            },
            sort: [
                {
                    name: 'date',
                    direction: SortEnumType.Asc,
                    id: uuid()
                }
            ]
        },
        header: {
          hideSearchbox: true
        },
        events: {
            onReload: async () => {
                return await loadData();
            }
        },
        itemId(i) {
            return i.id;
        },
        name: 'harvests-multi-upload-page',
        table: {
            columns: [
                {
                    name: 'date',
                    type: 'date',
                    card: {
                        icon: faCalendar,
                        group: 'header'
                    },
                    sort: {
                        resolve(direction) {
                            return {
                                work: {
                                    startDate: direction
                                }
                            }
                        },
                    },
                    resolve: (i) => i.id === TOTAL_ROW_INDEX ? t('fields.total.name') : resolveItemViewValue('startDate', i.work, 'date')
                },
                {
                    card: {
                        icon: faPeopleGroup,
                        group: 'header',
                    },
                    name: 'workersCount',
                    resolve: i => i.id === TOTAL_ROW_INDEX ? (formatMoney(i.kg) + ' KG') : i.work?.workers?.count
                },
                {
                    name: 'kg',
                    card: {
                        group: 'inputs',
                    },
                    resolve: (i, index) => {
                        return <Input
                            name="kg"
                            hideErrorMessages
                            selectTextOnFocus
                            type="number"
                            wrapperClassName="min-w-44"
                            required={true}
                            value={i.id === TOTAL_ROW_INDEX ? kgPerCart : data[index].kg?.toString()}
                            onBlur={v => {
                                if(i.id === TOTAL_ROW_INDEX) setTimeout(() => setKgPerCart(''), 100);
                                else if(!v || v === '0') onChangeValue(index, {kg: ''})
                            }}
                            prefix={{icon: faWeightHanging, name: "icon-prefix"}}
                            suffix={{ icon: faCalculator, onClick: () => calculateKg(index), name: t('common.calculate') }}
                            onChange={v => {
                                if(i.id === TOTAL_ROW_INDEX) {
                                    setKgPerCart(v || '');
                                    // const nr = Number(v);
                                    // if(nr) onKgPerCartChange(nr);
                                }
                                else onChangeValue(index, {kg: v});
                            }}
                            placeholder="fields.kg.shortName"
                        />
                    }
                },
                {
                    card: {
                        group: 'inputs',
                        title: false
                    },
                    name: 'cartQuantity',
                    title: 'fields.cart-quantity.shortName',
                    resolve: (i, index) => {
                        if(i.id === TOTAL_ROW_INDEX) return <div><FontAwesomeIcon icon={faBasketShopping} /> {data[index].cartQuantity}</div>;
                        return <Input
							name="cartQuantity"
							type="number"
                            hideErrorMessages
                            wrapperClassName="min-w-44"
							selectTextOnFocus
                            prefix={{icon: faBasketShopping, name: "icon--prefix"}}
							value={data[index]?.cartQuantity?.toString()}
							onChange={v => onChangeValue(index, { cartQuantity: v as any })}
							placeholder="fields.cart-quantity.shortName"
						/>
                    },
                },
                {
                    name: 'fruit',
                    card: {
                        group: 'footer-1',
                        icon: faSeedling
                    },
                    resolve: i => i.fruit?.name
                },
                {
                    name: 'category',
                    card: {
                        group: 'footer-1',
                        icon: faLayerGroup,
                    },
                    resolve: i => i.harvestFruitCategories?.items?.map(i => i.fruitCategory.name).join(', ')
                },
                {
                    name: 'group',
                    card: {
                        group: 'footer-1',
                        icon: faObjectGroup
                    },
                    resolve: i => i.harvestFruitGroups?.items?.map(i => i.fruitGroup.name).join(', ')
                },
                {
                    name: 'season',
                    card: {
                        group: 'header',
                        icon: faTimeline,
                        groupOrder: 2,
                        resolve(i, index) {
                            return "S" + i.work?.season?.name.replace('Season 20', '');
                        },
                    },
                    resolve: i => i.work?.season?.name
                },
                {
                    name: 'hideFromMultiUpdate',
                    title: '',
                    card: {
                        group: 'footer',
                        groupOrder: 1
                    },
                    resolve(i, index) {
                        return i.hideFromMultiUpdate 
                            ? <></> 
                            : <Button onClick={() => markAsDone(i, index)} theme="link" className="text-danger" title="common.hide">
                                <FontAwesomeIcon icon={faCheckToSlot} />
                            </Button>
                    }
                },
				{
					name: 'createdAt',
                    card: {
                        group: 'footer',
                        groupOrder: 3
                    },
					title: '',
					resolve(i, index) {
						return <Button onClick={() => save(index)} theme="link" className="text-success" title={t(i.id === TOTAL_ROW_INDEX ? 'button.save-all' : 'button.save')}>
							<FontAwesomeIcon icon={faSave} />
						</Button>
					},
				},
				{
					name: 'open',
                    card: {
                        group: 'footer',
                        groupOrder: 2
                    },
					title: '',
					resolve(i, index) {
						return <AppLink to={AppRoutes.AGRICULTURE_WORK_VIEW} params={{ id: i.work?.id }} title="common.view" className="text-primary">
                            <FontAwesomeIcon icon={faEye} />
                        </AppLink>
					},
				}
            ],
        }
    }

    // #region loadData
    useEffect(() => {
        loadData().then();
    }, []);

    const loadData = async (params?: DataSourceRequestType<HarvestFilterInput, HarvestSortInput>) => {
        setComponentState('loading');
        let variables: HarvestMultiUploadDataQueryVariables = {
            where: {
                ...params?.filters,
                kg: {
                    eq: 0
                },
                hideFromMultiUpdate: {
                    eq: false
                }
            },
            order: params?.sort || [{
                work: {
                    startDate: SortEnumType.Asc
                }
            }],
        }

        const request = await graph<HarvestMultiUploadDataQuery, HarvestMultiUploadDataQueryVariables>({
            query,
            variables
        });

        if(request.errors) {
            setComponentState('error');
            throw request.errors;
        }

        if(request.data?.harvests?.items?.length) {
            setTitle(t('pages.agriculture.work.harvest-multi-update') + ': ' + request.data?.harvests?.items?.length);

            let items: Partial<HarvestType>[];

            items = request.data.harvests.items.map((i: any) => {
                return {
                    ...i,
                    kg: i!.kg || '',
                    cartQuantity: i?.cartQuantity || '',
                    realKg: i!.kg || 0,
                } as HarvestType;
            });

            items.push({
                cartQuantity: request.data.harvests.items.reduce((a, b) => a + (b?.cartQuantity ?? 0), 0),
                id: TOTAL_ROW_INDEX,
                kg: request.data.harvests.items.reduce((a, b) => a + (b?.kg ?? 0), 0),
                realKg: 0
            });

            setData(items as HarvestType[]);

        } else {
            setTitle(t('pages.agriculture.work.harvest-multi-update'));
            setData([]);
        }

        setComponentState('');
        setKgPerCart('');
    }
    // #endregion loadData

    const onChangeValue = (index: number, value: Partial<HarvestType>) => {
        if(data[index].id === TOTAL_ROW_INDEX) return;
        data[index] = {
            ...data[index],
            ...value
        };

        updateTotal(data);
        setData([...data]);

        datalistRef.current?.refresh();
    }

    const onKgPerCartChange = (kgPerCart: number) => {
        data.forEach((i) => {
            if(i.id === TOTAL_ROW_INDEX) return;
            i.kg = (Number(i.cartQuantity) || 0) * kgPerCart;
        });

        updateTotal(data);

        setData([...data]);
        setKgPerCart('');
        datalistRef.current?.refresh();
    }

    const calculateKg = (index: number) => {
        if(data[index].id === TOTAL_ROW_INDEX) {
            const nr = Number(kgPerCart);
            if(nr) onKgPerCartChange(nr);
        }
        else if(Number(data[index].cartQuantity)) {
            const kg = Number(data[index].kg) * Number(data[index].cartQuantity);
            onChangeValue(index, { kg })
        }
    }

    const updateTotal = (theData: HarvestType[]) => {
        theData[data.length - 1] = {
            ...data[data.length - 1],
            kg: data.filter(x => x.id !== TOTAL_ROW_INDEX).reduce((a, b) => Number(a) + (Number(b?.kg) ?? 0), 0),
            cartQuantity: data.filter(x => x.id !== TOTAL_ROW_INDEX).reduce((a, b) => Number(a) + (Number(b?.cartQuantity) ?? 0), 0) 
        }
    }
    
    const markAsDone = async (item: HarvestType, index: number) => {
        try {
            const response = await api.post('/harvest/SetHideOnMasUpdate', { id: item.id });

            if(response.success) {
                context.notify('success', t('form.submit.success'));
                await loadData();
            } else {
                context.notify('error', t(`request.${response.statusText}`));
            }
        } catch (e) {
            context.notify('error', t('form.submit.error'));
            console.error(e);
            throw e;
        }
    }

    const save = async (index: number) => {
		let saveData: HarvestMultiUpdateInput[] = [];
		if (data[index].id !== TOTAL_ROW_INDEX) {
			saveData.push({
                id: data[index].id,
                cartQuantity: Number(data[index].cartQuantity),
                kg: Number(data[index].kg)
            });
		} else {
			data.filter(x => x.id !== TOTAL_ROW_INDEX).forEach(i => {
                saveData.push({
                    id: i.id,
                    cartQuantity: Number(i.cartQuantity),
                    kg: Number(i.kg)
                });
            });
		}

		saveData = saveData.filter(i => i.kg > 0 && i.id > 0 && i.cartQuantity > 0);
		if (data.length === 0) {
			context.notify('info', t('common.no-data-to-save'));
			return;
		}

		try {
			const response = await graph<HarvestMultiUpdateMutation, HarvestMultiUpdateMutationVariables>({
				query: harvestMultiUpload,
				variables: {
					data: saveData
				}
			});

			if (response.errors) {
				throw response.errors;
			}

			context.notify('success', t('form.submit.success'));
			await loadData()
		} catch (e) {
			context.notify('error', t('form.submit.error'));
            await loadData()
		}
    }

    return (
        <Page wFull title={title} actions={pageActions} state={componentState}>
            <div className="py-2">
                <Datalist
                    ref={datalistRef}
                    {...datalistConfig}
                />
            </div>
        </Page>
    );
};

export default HarvestMultiUploadPage;
